import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  addToast,
} from "@heroui/react";
import { LimeAlert } from "@/Components/NextBase/LimeAlert";
import { LimeDateRangePicker } from "@/Components/NextBase/LimeDateRangePicker";
import { LimeLoader } from "@/Components/NextBase/LimeLoader";
import { LimeSwitch } from "@/Components/NextBase/LimeSwitch";
import { Trans } from "@lingui/react/macro";
import { t } from "@lingui/core/macro";
import * as Sentry from "@sentry/react";
import { api } from "@/lib/api-client";
import type { GetCustomerSubscriptionInvoiceById } from "@/server-types";
import {
  computeFutureInvoicePeriodCorrections,
  formatPeriodRange,
  getEditPeriodDates,
  isEditPeriodRangeValid,
  type EditPeriodCalendarRange,
  type FuturePeriodCorrection,
} from "./subscriptionInvoiceUtils";
import { useMemo, useState } from "react";
import { LimeNumberInput } from "@/Components/NextBase/LimeNumberInput";
import { useForm } from "@mantine/form";
import { CalendarDate, fromDate } from "@internationalized/date";
import { returnCurrencySymbol } from "../../../../../../shared/utils/utils";
import { getApiErrorMessage } from "@/utils/api-error";
import { CommitmentWarningDialog } from "@/Components/CommitmentWarningDialog";

type Invoice = GetCustomerSubscriptionInvoiceById["response"]["invoice"];

type Props = {
  invoiceId: number | null;
  customerSubscriptionId: number;
  onClose: () => void;
};

function toCalendarDateUtc(date: Date | string): CalendarDate {
  const z = fromDate(new Date(date), "UTC");
  return new CalendarDate(z.year, z.month, z.day);
}

type FormProps = {
  invoiceId: number;
  customerSubscriptionId: number;
  invoice: Invoice;
  onClose: () => void;
};

function FuturePeriodsPreview({
  corrections,
}: {
  corrections: FuturePeriodCorrection[];
}) {
  if (corrections.length === 0) {
    return (
      <LimeAlert
        color="primary"
        message={t`Prihodnja obdobja ostanejo nespremenjena`}
      />
    );
  }

  return (
    <div className="border-default-200 flex max-h-56 flex-col gap-3 overflow-y-auto rounded-lg border p-3">
      <p className="text-tiny text-default-400">
        <Trans>Prihodnja obdobja se bodo spremenila</Trans>
      </p>
      {corrections.map((correction) => (
        <div key={correction.invoiceId} className="text-small flex flex-col">
          <span className="text-default-400 line-through">
            {formatPeriodRange(
              correction.currentPeriodStart,
              correction.currentPeriodEnd,
            )}
          </span>
          <span>
            {formatPeriodRange(correction.periodStart, correction.periodEnd)}
          </span>
        </div>
      ))}
    </div>
  );
}

function EditPeriodForm({
  invoiceId,
  customerSubscriptionId,
  invoice,
  onClose,
}: FormProps) {
  const { mutateAsync: updateInvoice, isPending: isUpdating } =
    api.customer.useUpdateSubscriptionInvoice();

  const { data: allInvoicesData } = api.customer.useGetSubscriptionInvoices({
    customerSubscriptionId,
    query: { pagination: { page: 1, perPage: 100 } },
  });

  const { data: subscriptionData } = api.customer.useGetCustomerSubscription({
    customerSubscriptionId,
  });
  const subscription = subscriptionData?.customerSubscription;
  const commitmentLock = subscription?.commitmentLock;
  const isPriceLockedByCommitment =
    commitmentLock?.priceLockedPeriodIds.includes(invoiceId) ?? false;
  const areDatesLockedByCommitment =
    commitmentLock?.chainLockedPeriodIds.includes(invoiceId) ?? false;

  const { data: currency } = api.values.useGetCurrency({});
  const currencySymbol = currency ? returnCurrencySymbol({ currency }) : "";

  const form = useForm<{
    dateRange: EditPeriodCalendarRange;
    price: number;
    correctFutureInvoices: boolean;
  }>({
    initialValues: {
      dateRange: {
        start: toCalendarDateUtc(invoice.periodStart),
        end: toCalendarDateUtc(invoice.periodEnd),
      },
      price: invoice.basePriceCents / 100,
      correctFutureInvoices: false,
    },
    validate: {
      dateRange: (value) =>
        !value
          ? t`Izberite obdobje.`
          : value.end.compare(value.start) < 0
            ? t`Končni datum ne sme biti pred začetnim datumom`
            : null,
      price: (value) => (value < 0 ? t`Cena ne sme biti negativna` : null),
    },
  });

  const periodRange = getEditPeriodDates(form.values.dateRange);
  const isDateRangeValid = isEditPeriodRangeValid(periodRange);
  const overlappingInvoice = periodRange
    ? (allInvoicesData?.invoices ?? []).find(
        (inv) =>
          inv.id !== invoiceId &&
          new Date(inv.periodStart) < periodRange.end &&
          new Date(inv.periodEnd) > periodRange.start,
      )
    : undefined;

  const isPreviewEnabled =
    form.values.correctFutureInvoices && isDateRangeValid;
  const newStartTime = periodRange?.start.getTime() ?? null;
  const newEndTime = periodRange?.end.getTime() ?? null;
  const corrections = useMemo(() => {
    const invoices = allInvoicesData?.invoices;
    if (
      !isPreviewEnabled ||
      !invoices ||
      !subscription ||
      newStartTime === null ||
      newEndTime === null
    )
      return [];

    return computeFutureInvoicePeriodCorrections({
      invoices,
      editedInvoiceId: invoiceId,
      periodStart: new Date(newStartTime),
      periodEnd: new Date(newEndTime),
      unit: subscription.durationUnit,
      durationValue: subscription.durationValue,
    });
  }, [
    isPreviewEnabled,
    allInvoicesData?.invoices,
    subscription,
    invoiceId,
    newStartTime,
    newEndTime,
  ]);
  const correctsCommittedPeriod = corrections.some((correction) =>
    commitmentLock?.chainLockedPeriodIds.includes(correction.invoiceId),
  );

  const [isWarningOpen, setIsWarningOpen] = useState(false);

  const touchesCommitment =
    isPriceLockedByCommitment ||
    areDatesLockedByCommitment ||
    correctsCommittedPeriod;

  const handleSave = () => {
    if (form.validate().hasErrors) return;
    if (touchesCommitment) {
      setIsWarningOpen(true);
      return;
    }
    void savePeriod();
  };

  const savePeriod = async () => {
    const { dateRange, price, correctFutureInvoices } = form.getValues();
    if (!dateRange) return;

    try {
      await updateInvoice({
        invoiceId,
        body: {
          periodStart: dateRange.start.toDate("UTC"),
          periodEnd: dateRange.end.toDate("UTC"),
          basePriceCents: Math.round(price * 100),
          correctFutureInvoices,
        },
      });

      addToast({
        title: t`Obdobje računa je bilo uspešno posodobljeno`,
        color: "success",
      });

      onClose();
    } catch (error) {
      Sentry.captureException(error);
      addToast({
        title: t`Napaka pri posodabljanju obdobja računa`,
        description:
          getApiErrorMessage(error) ??
          t`Prišlo je do napake. Poskusite ponovno.`,
        color: "danger",
      });
    } finally {
      setIsWarningOpen(false);
    }
  };

  const handleClose = () => {
    form.reset();
    onClose();
  };

  return (
    <>
      <ModalHeader>
        <Trans>Uredi obdobje</Trans>
      </ModalHeader>
      <ModalBody>
        <div className="flex flex-col gap-4">
          <LimeDateRangePicker
            label={t`Izberi obdobje`}
            variant="bordered"
            labelPlacement="outside"
            error={
              typeof form.errors.dateRange === "string"
                ? form.errors.dateRange
                : undefined
            }
            data-identifier="edit-period-date-range-input"
            description={
              areDatesLockedByCommitment ? t`Obdobje je del vezave.` : undefined
            }
            {...form.getInputProps("dateRange")}
          />
          <LimeNumberInput
            label={t`Cena`}
            labelPlacement="outside"
            minValue={0}
            step={0.01}
            endContent={currencySymbol}
            data-identifier="edit-period-price-input"
            description={
              isPriceLockedByCommitment
                ? t`Cena tega obdobja je del vezave.`
                : undefined
            }
            {...form.getInputProps("price")}
          />
          <LimeAlert
            color="warning"
            message={
              overlappingInvoice
                ? t`Izbrano obdobje se prekriva z obstoječim obdobjem (${overlappingInvoice.periodStartFormatted} – ${overlappingInvoice.periodEndFormatted})`
                : null
            }
          />
          <LimeSwitch
            isCompact
            title={t`Popravi prihodnja obdobja glede na konec tega obdobja`}
            description={t`Vsa naslednja obdobja se poravnajo, tudi če so že plačana ali imajo izdan predračun.`}
            data-identifier="edit-period-correct-future-periods-switch"
            {...form.getInputProps("correctFutureInvoices", {
              type: "checkbox",
            })}
          />
          {isPreviewEnabled && (
            <FuturePeriodsPreview corrections={corrections} />
          )}
          {correctsCommittedPeriod && (
            <LimeAlert
              color="warning"
              data-identifier="edit-period-corrects-committed-alert"
              message={t`Poravnava bo premaknila obdobja, ki so del vezave.`}
            />
          )}
        </div>
      </ModalBody>
      <ModalFooter>
        <Button variant="light" onPress={handleClose}>
          <Trans>Prekliči</Trans>
        </Button>
        <Button
          color="primary"
          onPress={handleSave}
          isLoading={isUpdating}
          data-identifier="subscription-invoice-edit-period-save-button"
        >
          <Trans>Shrani</Trans>
        </Button>
      </ModalFooter>
      <CommitmentWarningDialog
        isOpen={isWarningOpen}
        isSaving={isUpdating}
        onDismiss={() => setIsWarningOpen(false)}
        onConfirm={() => {
          void savePeriod();
        }}
      />
    </>
  );
}

export function SubscriptionInvoiceEditPeriodModal({
  invoiceId,
  customerSubscriptionId,
  onClose,
}: Props) {
  const { data, isPending, isError, processedErrorMessage } =
    api.customer.useGetSubscriptionInvoiceById({
      invoiceId,
    });

  return (
    <Modal
      isOpen={invoiceId != null}
      onClose={onClose}
      data-identifier="subscription-invoice-edit-period-modal"
    >
      <ModalContent>
        {() => {
          if (isError) {
            return (
              <>
                <ModalHeader>
                  <Trans>Uredi obdobje</Trans>
                </ModalHeader>
                <ModalBody>
                  <LimeAlert
                    color="danger"
                    message={
                      processedErrorMessage ??
                      t`Prišlo je do napake. Poskusite ponovno.`
                    }
                  />
                </ModalBody>
                <ModalFooter>
                  <Button variant="light" onPress={onClose}>
                    <Trans>Zapri</Trans>
                  </Button>
                </ModalFooter>
              </>
            );
          }
          if (isPending || !data || invoiceId == null) {
            return (
              <ModalBody className="py-8">
                <LimeLoader />
              </ModalBody>
            );
          }
          return (
            <EditPeriodForm
              key={invoiceId}
              invoiceId={invoiceId}
              customerSubscriptionId={customerSubscriptionId}
              invoice={data.invoice}
              onClose={onClose}
            />
          );
        }}
      </ModalContent>
    </Modal>
  );
}
